import React, { useMemo } from 'react';
import {
  Settings2, X, BarChart3, TrendingUp, Activity, PieChart,
  Filter, Hash, Grip, Table2,
} from 'lucide-react';
import { useWidgetData } from '../../hooks/useDashboards';
import {
  AGGREGATIONS, CHART_KINDS, isTableChart, usesCategoryAxis, usesValueAxis,
  eligibleCategoryFields, eligibleValueFields,
} from '../../../analytics-shared/engine/widgetSchema';
import { displayLabel } from '../../../analytics-shared/engine/reportEngine';

/**
 * Right-hand inspector for the dashboard builder.
 *
 * The panel is **driven by the live report schema** of the selected
 * widget: when the widget's report exposes fields A/B/C, the X-Axis and
 * Y-Axis dropdowns only surface those fields, with smart type filtering
 * (categoricals on the X side, numerics on the Y side). The panel adapts
 * the section labels per chart kind — pie/donut/heatmap surface
 * "Category / Value" while bar/line/area show "X-Axis / Y-Axis", KPI hides
 * the category entirely, and table swaps the axes for a column picker.
 *
 * Anything edited here flows back through onUpdate(widget) → the
 * useWidgetData query key changes → the chart re-aggregates from the same
 * cached record set. No extra network round-trip per keystroke.
 */

const CHART_TYPE_BUTTONS = [
  { value: 'bar',     icon: BarChart3 },
  { value: 'line',    icon: TrendingUp },
  { value: 'area',    icon: Activity },
  { value: 'pie',     icon: PieChart },
  { value: 'donut',   icon: PieChart },
  { value: 'funnel',  icon: Filter },
  { value: 'heatmap', icon: Grip },
  { value: 'kpi',     icon: Hash },
  { value: 'table',   icon: Table2 },
];

const SectionLabel = ({ children, hint }) => (
  <div className="flex items-baseline justify-between mb-2">
    <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400">{children}</p>
    {hint && <span className="text-[10px] text-slate-300">{hint}</span>}
  </div>
);

const Toggle = ({ checked, onChange, label }) => (
  <label className="flex items-center gap-2.5 cursor-pointer group">
    <div
      onClick={onChange}
      className={`w-8 flex items-center rounded-full transition-colors cursor-pointer px-0.5 flex-shrink-0 ${
        checked ? 'bg-indigo-600' : 'bg-slate-200'
      }`}
      style={{ height: '18px' }}
    >
      <div className={`w-3.5 h-3.5 bg-white rounded-full shadow transition-transform ${checked ? 'translate-x-3.5' : 'translate-x-0'}`} />
    </div>
    <span className="text-xs text-slate-600 group-hover:text-slate-800 select-none">{label}</span>
  </label>
);

const Select = ({ value, onChange, options, placeholder, disabled }) => (
  <select
    value={value || ''}
    disabled={disabled}
    onChange={(e) => onChange(e.target.value || null)}
    className={`w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50 focus:bg-white transition-colors ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
  >
    <option value="">{placeholder}</option>
    {options.map((opt) => (
      <option key={opt.value} value={opt.value}>{opt.label}</option>
    ))}
  </select>
);

const TextInput = ({ value, onChange, placeholder }) => (
  <input
    value={value || ''}
    onChange={(e) => onChange(e.target.value)}
    placeholder={placeholder}
    className="w-full px-3 py-2 text-xs border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-slate-50 focus:bg-white transition-colors"
  />
);

const EmptyState = () => (
  <div className="flex flex-col h-full bg-white border-l border-slate-200">
    <div className="flex flex-col items-center justify-center flex-1 p-6 text-center gap-3">
      <div className="w-12 h-12 rounded-2xl bg-slate-50 border border-slate-200 flex items-center justify-center">
        <Settings2 className="w-6 h-6 text-slate-300" />
      </div>
      <div>
        <p className="text-sm font-semibold text-slate-500">Widget Settings</p>
        <p className="text-xs text-slate-400 mt-0.5">Click a widget on the canvas to configure it</p>
      </div>
    </div>
  </div>
);

const WidgetSettingsPanel = ({ widget, onUpdate, onClose }) => {
  // useWidgetData is shared with the canvas — it fetches report metadata +
  // live records + the field catalogue. The settings panel reads `fields`
  // off the same query so the dropdowns only show fields the widget can
  // actually visualise. Cached, so it doesn't refire when the panel opens.
  const { data: widgetData, isLoading } = useWidgetData(widget);

  const fields = widgetData?.fields || [];
  const reportName = widgetData?.report?.name;
  const reportObject = widgetData?.object?.label || widget?.report?.object;
  const cfg = widget?.config || {};

  // Smart field buckets per axis.
  const categoryFields = useMemo(() => eligibleCategoryFields(fields), [fields]);
  const valueFields = useMemo(() => eligibleValueFields(fields), [fields]);

  const kindMeta = CHART_KINDS[widget?.chartType] || CHART_KINDS.bar;

  if (!widget) return <EmptyState />;

  const update = (partial) => onUpdate({ ...widget, ...partial });
  const updateConfig = (partial) => onUpdate({ ...widget, config: { ...widget.config, ...partial } });

  // Salesforce-style qualified labels: primary fields show bare ("City"),
  // related fields get the source-object name in parens ("City (Lead)").
  // Same rule applied across chips, table headers, chart axis labels.
  const categoryOptions = categoryFields.map((f) => ({ value: f.key, label: displayLabel(f) || f.label }));
  const valueOptions = valueFields.map((f) => ({ value: f.key, label: displayLabel(f) || f.label }));
  // For COUNT we don't need a value field, so guide the UI accordingly.
  const valueRequired = cfg.aggregate !== 'COUNT';

  const showCategoryAxis = usesCategoryAxis(widget.chartType);
  const showValueAxis = usesValueAxis(widget.chartType);

  return (
    <div className="flex flex-col h-full bg-white border-l border-slate-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-100 flex-shrink-0 bg-gradient-to-r from-indigo-50/60 to-white">
        <div className="flex items-center gap-2 min-w-0">
          <div className="w-6 h-6 rounded-md bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <Settings2 className="w-3.5 h-3.5 text-indigo-600" />
          </div>
          <div className="min-w-0">
            <h2 className="text-xs font-bold text-slate-700 uppercase tracking-wide truncate">Widget Settings</h2>
            {reportName && (
              <p className="text-[10px] text-slate-400 truncate">
                {reportName}{reportObject ? ` · ${reportObject}` : ''}
              </p>
            )}
          </div>
        </div>
        <button onClick={onClose} className="p-1 hover:bg-slate-100 rounded-lg transition-colors text-slate-400 hover:text-slate-600">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-5">
        <div>
          <SectionLabel>Title</SectionLabel>
          <TextInput value={widget.title} onChange={(v) => update({ title: v })} placeholder="Widget title…" />
        </div>

        <div>
          <SectionLabel>Subtitle</SectionLabel>
          <TextInput value={widget.subtitle} onChange={(v) => update({ subtitle: v })} placeholder="Optional description…" />
        </div>

        <div>
          <SectionLabel>Chart Type</SectionLabel>
          <div className="grid grid-cols-3 gap-1.5">
            {CHART_TYPE_BUTTONS.map((t) => {
              const Icon = t.icon;
              const active = widget.chartType === t.value;
              return (
                <button key={t.value} onClick={() => update({ chartType: t.value })}
                  className={`flex items-center justify-center gap-1.5 px-2 py-2 text-[11px] rounded-lg border transition-all ${
                    active
                      ? 'bg-indigo-600 text-white border-indigo-600 shadow-sm shadow-indigo-200'
                      : 'bg-white text-slate-600 border-slate-200 hover:border-indigo-200 hover:bg-indigo-50 hover:text-indigo-700'
                  }`}>
                  <Icon className="w-3 h-3 flex-shrink-0" />
                  {CHART_KINDS[t.value]?.label || t.value}
                </button>
              );
            })}
          </div>
        </div>

        {isTableChart(widget.chartType) ? (
          <TableColumnsSection
            fields={fields}
            selected={cfg.tableColumns || []}
            onChange={(cols) => updateConfig({ tableColumns: cols })}
            isLoading={isLoading}
          />
        ) : (
          <>
            {showCategoryAxis && (
              <div>
                <SectionLabel hint={isLoading ? 'Loading…' : `${categoryFields.length} fields`}>
                  {kindMeta.axes.x}
                </SectionLabel>
                <Select
                  value={cfg.xField}
                  onChange={(v) => updateConfig({ xField: v })}
                  options={categoryOptions}
                  placeholder={isLoading ? 'Loading fields…' : `Select ${kindMeta.axes.x.toLowerCase()}…`}
                  disabled={isLoading || !categoryFields.length}
                />
                {!isLoading && !categoryFields.length && (
                  <p className="text-[10px] text-slate-400 mt-1">No categorical fields on this report.</p>
                )}
              </div>
            )}

            {showValueAxis && (
              <>
                <div className="grid grid-cols-5 gap-1.5">
                  <div className="col-span-3">
                    <SectionLabel>{kindMeta.axes.y}</SectionLabel>
                    <Select
                      value={cfg.yField}
                      onChange={(v) => updateConfig({ yField: v })}
                      options={valueOptions}
                      placeholder={valueRequired ? 'Select value field…' : 'Optional (Count)'}
                      disabled={isLoading || (!valueRequired && !valueFields.length)}
                    />
                  </div>
                  <div className="col-span-2">
                    <SectionLabel>Aggregate</SectionLabel>
                    <Select
                      value={cfg.aggregate || 'COUNT'}
                      onChange={(v) => updateConfig({ aggregate: v })}
                      options={AGGREGATIONS.map((a) => ({ value: a.value, label: a.label }))}
                      placeholder="Aggregate"
                    />
                  </div>
                </div>
                {valueRequired && !cfg.yField && !isLoading && valueFields.length === 0 && (
                  <p className="text-[10px] text-amber-600 mt-1">
                    This report has no numeric fields. Switch the aggregate to Count, or pick a different report.
                  </p>
                )}
              </>
            )}

            {(showCategoryAxis || showValueAxis) && (
              <div className="grid grid-cols-2 gap-2">
                {showCategoryAxis && (
                  <div>
                    <SectionLabel>{kindMeta.axes.x} Label</SectionLabel>
                    <TextInput
                      value={cfg.xAxisLabel}
                      onChange={(v) => updateConfig({ xAxisLabel: v })}
                      placeholder={kindMeta.axes.x}
                    />
                  </div>
                )}
                {showValueAxis && (
                  <div>
                    <SectionLabel>{kindMeta.axes.y} Label</SectionLabel>
                    <TextInput
                      value={cfg.yAxisLabel}
                      onChange={(v) => updateConfig({ yAxisLabel: v })}
                      placeholder={kindMeta.axes.y}
                    />
                  </div>
                )}
              </div>
            )}
          </>
        )}

        <div>
          <SectionLabel>Display Options</SectionLabel>
          <div className="space-y-3 bg-slate-50 rounded-xl p-3">
            <Toggle
              checked={cfg.showTitle !== false}
              onChange={() => updateConfig({ showTitle: !(cfg.showTitle !== false) })}
              label="Show title"
            />
            <Toggle
              checked={cfg.drillDownEnabled !== false}
              onChange={() => updateConfig({ drillDownEnabled: !(cfg.drillDownEnabled !== false) })}
              label="Enable drill-down"
            />
            {!isTableChart(widget.chartType) && (
              <>
                <Toggle
                  checked={cfg.showLegend !== false}
                  onChange={() => updateConfig({ showLegend: !(cfg.showLegend !== false) })}
                  label="Show legend"
                />
                <Toggle
                  checked={cfg.showGrid !== false}
                  onChange={() => updateConfig({ showGrid: !(cfg.showGrid !== false) })}
                  label="Show grid"
                />
                <Toggle
                  checked={cfg.showTooltip !== false}
                  onChange={() => updateConfig({ showTooltip: !(cfg.showTooltip !== false) })}
                  label="Show tooltips"
                />
              </>
            )}
          </div>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-3">
          <SectionLabel>Size on Canvas</SectionLabel>
          <div className="flex items-center gap-4 text-xs">
            <div className="text-center">
              <p className="text-lg font-bold text-slate-800 leading-none">{widget.layout?.w || '—'}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">columns</p>
            </div>
            <span className="text-slate-300 text-lg font-light">×</span>
            <div className="text-center">
              <p className="text-lg font-bold text-slate-800 leading-none">{widget.layout?.h || '—'}</p>
              <p className="text-[10px] text-slate-400 mt-0.5">rows</p>
            </div>
          </div>
          <p className="text-[10px] text-slate-400 mt-2.5 leading-relaxed">
            Drag corner handles on the canvas to resize
          </p>
        </div>
      </div>
    </div>
  );
};

// Table-widget column picker: checkbox list of every report field. If
// nothing is checked, the widget falls back to the report's saved columns
// at render time (see useWidgetData) — that "inherit from report" behaviour
// is what makes table widgets match the report's columns by default.
const TableColumnsSection = ({ fields, selected, onChange, isLoading }) => {
  const isInheriting = !selected.length;
  const toggle = (key) => {
    const next = selected.includes(key)
      ? selected.filter((k) => k !== key)
      : [...selected, key];
    onChange(next);
  };
  return (
    <div>
      <SectionLabel hint={isInheriting ? 'inheriting from report' : `${selected.length} selected`}>
        Columns
      </SectionLabel>
      {isLoading ? (
        <p className="text-xs text-slate-400">Loading fields…</p>
      ) : (
        <>
          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-lg bg-slate-50">
            {fields.length === 0 && (
              <p className="text-[11px] text-slate-400 px-3 py-2">This report has no fields yet.</p>
            )}
            {fields.map((f) => (
              <label key={f.key} className="flex items-center gap-2 px-3 py-1.5 hover:bg-white cursor-pointer">
                <input
                  type="checkbox"
                  checked={selected.includes(f.key)}
                  onChange={() => toggle(f.key)}
                  className="accent-indigo-600"
                />
                <span className="text-xs text-slate-700 flex-1 truncate">{displayLabel(f) || f.label}</span>
                <span className="text-[10px] text-slate-400 uppercase">{f.type}</span>
              </label>
            ))}
          </div>
          {isInheriting && (
            <p className="text-[10px] text-slate-400 mt-1">
              Nothing selected → widget inherits the report's columns at render time.
            </p>
          )}
        </>
      )}
    </div>
  );
};

export default WidgetSettingsPanel;
